import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { AuthenticationService } from '../../authentication/authentication/authentication.service';
import { RecipeService } from 'src/app/services/recipe.service';
import { ToastrService } from 'ngx-toastr';
import { ProductRecipe, RecipesResponse } from 'src/app/models/recipe-response';
import { OrgChart } from 'd3-org-chart';
import { ProductResponse } from 'src/app/models/product.model';


interface OrgNode {
  id: string;
  parentId: string | null;
  parent: any;
  name: string;
  type: string;
  image?: string;
  data: any;
  quantity: number;
  quantityChilds: number;
  position: string;
}

@Component({
  selector: 'app-recipe-diagram',
  templateUrl: './recipe-diagram.component.html',
  styleUrls: ['./recipe-diagram.component.scss']
})
export class RecipeDiagramComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  dataChart: OrgNode[] = [];
  actualCompany: any;
  productRecipes: RecipesResponse[] = [];
  filterProductRecipes: RecipesResponse[] = [];
  private chart!: OrgChart<any>;

  constructor(
    private authService: AuthenticationService,
    private recipeService: RecipeService,
    private toastService: ToastrService
  ) {

  }

  ngOnInit(): void {
    this.getCompany();
  }

  ngAfterViewInit(): void {
    if (this.dataChart && this.dataChart.length) {
      this.renderChart();
    }
  }

  ngOnChanges(): void {
    if (this.chart && this.dataChart.length) {
      this.chart.data(this.dataChart).render();
    }
  }

  getCompany() {
    this.actualCompany = JSON.parse(this.authService.getCurrentCompany());
    this.getRecipes();
  }

  getRecipes() {
    this.recipeService.getRecipes().subscribe({
      next: (response: any) => {
        this.productRecipes = response.data;
        this.generateNodes();
      },
      error: (error) => {
        this.toastService.error("Error al cargar las recetas", "Error");
      }
    });
  }

  generateNodes() {
    const nodeCompany: OrgNode = {
      id: "0",
      parentId: null,
      parent: null,
      name: this.actualCompany.companyName,
      type: "company",
      image: this.actualCompany.companyLogo || '../../../../../assets/img/nophoto.svg',
      data: this.actualCompany,
      position: "Empresa",
      quantity: 0,
      quantityChilds: this.productRecipes.length,
    }

    this.dataChart = [nodeCompany];

    if (this.productRecipes.length > 0) {
      this.productRecipes.forEach((recipe, index) => {
        const nodeRecipe: OrgNode = {
          id: `0-${recipe.product.id}`,
          parentId: "0",
          parent: nodeCompany,
          name: recipe.product.name,
          type: "recipe",
          image: recipe.product.image || '../../../../../assets/img/nophoto.svg',
          data: recipe,
          quantity: 1,
          quantityChilds: recipe.listIngredients.length,
          position: "Receta",
        }

        this.dataChart.push(nodeRecipe);
        recipe.listIngredients.forEach(ingredient => {
          this.generateNodeIngredient(ingredient, nodeRecipe, `0-${recipe.product.id}`)
        });
      });
    }

    this.renderChart();
  }

  generateNodeIngredient(ingredientChild: ProductRecipe, parent: any, idParent: string) {
    this.dataChart.push({
      id: `${idParent}-${ingredientChild.productDataIngredient.id}`,
      parentId: idParent,
      parent: parent,
      name: ingredientChild.productDataIngredient.name,
      type: "ingredient",
      image: ingredientChild.productDataIngredient.image || '../../../../../assets/img/nophoto.svg',
      data: ingredientChild,
      quantity: ingredientChild.quantity,
      quantityChilds: 0,
      position: "Ingrediente",
    });
  }


  private renderChart(): void {
    if (!this.chartContainer?.nativeElement || !this.dataChart.length) return;

    const self = this;

    this.chart = new OrgChart()
      .container(this.chartContainer.nativeElement)
      .data(this.dataChart)
      .layout('left')
      .nodeWidth(() => 220)
      .childrenMargin(() => 50)
      .compact(false)
      .nodeContent((data: any) =>
        data.data.type === "company"
          ? self.generateChartCompany(data)
          : data.data.type === "recipe"
            ? self.generateChartRecipe(data)
            : self.generateChartIngredient(data)
      )
      .onExpandOrCollapse(() => {
        // 👇 Cuando expandes o colapsas, vuelve a conectar los inputs
        setTimeout(() => this.enableRecipeInputs(), 0);
      })
      .render();

    setTimeout(() => this.enableRecipeInputs(), 0);
  }


  // Nodo empresa
  generateChartCompany(d: any) {
    const color = '#FFFFFF';
    const imageDiffVert = 25 + 2;
    const maxSizeText = 45;
    const img = d.data.image.includes('company-avatar') ? '../../../../../assets/img/nophoto.svg' : d.data.image;
    const tempName = d.data.name.length > maxSizeText
      ? `${d.data.name.slice(0, maxSizeText)}...`
      : d.data.name;

    return `
      <div class='org-chart-data' style='width:${d.width}px;padding-left:1px;padding-right:1px'>
        <div class='data-company' style="font-family:'Nunito', sans-serif;background-color:${color};  margin-left:-1px;width:${d.width - 2}px;border-radius:10px;border: 1px solid #E4E2E9">
          <div style="display:flex;justify-content:flex-end;align-items: center;margin-top:5px;margin-right:8px; font-size: 1.5em;">
            <i class='fa fa-boxes'></i><b> ${d.data.quantityChilds}</b>
          </div>
          <div style="background-color:${color};margin-top:${-imageDiffVert - 20}px;margin-left:${15}px;border-radius:100px;width:50px;height:50px;"></div>
          <div style="margin-top:${-imageDiffVert - 20}px;">   
            <img src="${img}" style="margin-left:${20}px;border-radius:100px;width:40px;height:40px;" />
          </div>
          <div class='container-name' style="font-weight: 700;font-size:15px;color:#08011E;margin-left:20px;margin-top:10px">${tempName}</div>
          <div style="color:#716E7B;margin-left:20px;margin-top:3px;margin-bottom:4px;font-size:10px;"> ${d.data.position} </div>
        </div>
      </div>
    `;
  }

  // Nodo receta
  generateChartRecipe(d: any) {
    const color = '#FFFFFF';
    const imageDiffVert = 25 + 2;
    const img = d.data.image || '../../../../../assets/img/nophoto.svg';
    const name = d.data.name.length > 40 ? `${d.data.name.slice(0, 40)}...` : d.data.name;

    return `
      <div class='org-chart-data' style='width:${d.width}px;padding-left:1px;padding-right:1px'>
        <div class='data-company' style="font-family:'Nunito', sans-serif;background-color:${color};  margin-left:-1px;width:${d.width - 2}px;border-radius:10px;border: 1px solid #E4E2E9">
          <div style="display:flex;justify-content:flex-end;align-items: center;margin-top:5px;margin-right:8px; font-size: 1.5em;">
            <i class='fa fa-boxes'></i><b> ${d.data.quantityChilds}</b>
          </div>
          <div style="background-color:${color};margin-top:${-imageDiffVert - 20}px;margin-left:${15}px;border-radius:100px;width:50px;height:50px;"></div>
          <div style="margin-top:${-imageDiffVert - 20}px;">   
            <img src="${img}" style="margin-left:${20}px;border-radius:100px;width:40px;height:40px;" />
          </div>
          <div class='container-name' style="font-weight: 700;font-size:15px;color:#08011E;margin-left:20px;margin-top:10px">${name}</div>
          <div style="color:#716E7B;margin-left:20px;margin-top:3px;margin-bottom:4px;font-size:10px;"> ${d.data.position} </div>
          <div style="font-weight: 700;font-size:15px;color:#08011E;margin-left:20px;margin-top:10px">
            <input type="number" id="recipe-input-${d.id}" value="${d.data.quantity || 0}" data-recipe-id="${d.id}" />
          </div>
        </div>
      </div>
    `;
  }

  generateChartIngredient(d: any) {
    const color = '#FFFFFF';
    const imageDiffVert = 25 + 2;
    const img = d.data.image || '../../../../../assets/img/nophoto.svg';
    const name = d.data.name.length > 40 ? `${d.data.name.slice(0, 40)}...` : d.data.name;

    return `
      <div class='org-chart-data' style='width:${d.width}px;padding-left:1px;padding-right:1px'>
        <div class='data-company' style="font-family:'Nunito', sans-serif;background-color:${color};  margin-left:-1px;width:${d.width - 2}px;border-radius:10px;border: 1px solid #E4E2E9">
          <div style="display:flex;justify-content:flex-end;align-items: center;margin-top:5px;margin-right:8px; font-size: 1.5em;">
            
          </div>
          <div style="background-color:${color};margin-top:${-imageDiffVert - 20}px;margin-left:${15}px;border-radius:100px;width:50px;height:50px;"></div>
          <div style="margin-top:${-imageDiffVert - 20}px;">   
            <img src="${img}" style="margin-left:${20}px;border-radius:100px;width:40px;height:40px;" />
          </div>
          <div class='container-name' style="font-weight: 700;font-size:15px;color:#08011E;margin-left:20px;margin-top:10px">${name}</div>
          <div style="color:#716E7B;margin-left:20px;margin-top:3px;margin-bottom:4px;font-size:10px;"> ${d.data.position} </div>
          <div style="font-weight: 700;font-size:15px;color:#08011E;margin-left:20px;margin-top:10px">
            <input type="number" id="ingredient-input-${d.id}" value="${d.data.quantity || 0}" disabled />
          </div>
        </div>
      </div>
    `;
  }

  private enableRecipeInputs(): void {
    const inputs = this.chartContainer.nativeElement.querySelectorAll('input[type="number"]');

    inputs.forEach((input: HTMLInputElement) => {
      input.style.pointerEvents = 'all';
      input.style.position = 'relative';
      input.style.zIndex = '10';

      input.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      input.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const recipeId = target.dataset['recipeId'];
        const newValue = parseFloat(target.value) || 0;

        // 🔎 Buscar el nodo de receta correspondiente
        const node = this.dataChart.find(n => n.id === recipeId);
        if (!node) return;

        node.quantity = newValue;

        // 🔁 Actualizar los hijos (ingredientes)
        this.updateIngredientQuantities(node, newValue);
      });
    });
  }

  private updateIngredientQuantities(recipeNode: OrgNode, recipeQuantity: number): void {
    // 1️⃣ Buscar los ingredientes hijos
    const childNodes = this.dataChart.filter(n => n.parentId === recipeNode.id && n.type === 'ingredient');

    childNodes.forEach(child => {
      // 2️⃣ Calcular nueva cantidad
      const ingredientQuantity = (child.data.quantity || 0) * recipeQuantity;

      // 3️⃣ Actualizar el modelo
      child.quantity = ingredientQuantity;

      // 4️⃣ Actualizar el input en el DOM
      const input = this.chartContainer.nativeElement.querySelector(`#ingredient-input-${child.id}`) as HTMLInputElement;
      if (input) {
        input.value = ingredientQuantity.toFixed(2);
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    if (this.chart) {
      this.chart.fit(); // ajusta el zoom y el tamaño
    }
  }
}
